<template>
  <div>
    <CapableAppModal
      :modalHeading="modalHeading"
      :capableApps="capableApps"
      :configRequest="configRequest"
      v-if="showModal"
      @close="showModal = false"
      @completed="appAdded"
    />
    <ContentHighlightsEditModal
      v-if="showAppEditModal && editingAppIndex !== undefined"
      :app-config="apps[editingAppIndex]"
      @cancel="showAppEditModal = false"
      @save="(app: ConfiguredAppIntegration) => handleAppSave(app, editingAppIndex!)"
    />

    <n-button v-if="!isLozengeMode" variant="primary" @click="showModal = true">Add an app...</n-button>

    <template v-if="hasSelectedApps">
      <table v-if="!isLozengeMode" class="table is-fullwidth is-hoverable">
        <tbody>
          <Draggable
            @change="appsMoved"
            :list="apps"
            group="apps"
            ghost-class="ghost"
            class="draggable-table"
            item-key="installedAppId"
          >
            <template #item="{ element: item, index }">
              <tr :key="index" class="draggable-row">
                <td class="app-title-column">
                  <div class="app-name">{{ getName(item.installedAppId) }}</div>
                  <div class="app-description">{{ getDescription(item.installedAppId) }}</div>
                </td>
                <td class="action has-icon">
                  <div class="icon-container" v-if="canEditApp(item)">
                    <span @click="openContentHighlightsEditModal(index)">
                      <EditIcon />
                    </span>
                  </div>
                </td>
                <td class="action has-icon">
                  <div class="icon-container">
                    <svg @click="appRemoved(index)" class="trash-icon">
                      <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#trash" />
                    </svg>
                  </div>
                </td>
              </tr>
            </template>
          </Draggable>
        </tbody>
      </table>

      <div class="lozenge-mode" v-else>
        <Draggable
          @change="appsMoved"
          :list="apps"
          group="apps"
          ghost-class="ghost"
          class="draggable-table-lozenge"
          draggable=".draggable-row"
          item-key="installedAppId"
        >
          <template #item="{ element: item, index }">
            <div :key="index" class="draggable-row">
              <button class="button">
                <font-awesome-icon icon="cube"></font-awesome-icon>
                <span class="app-name">Send an app: {{ getName(item.installedAppId) }}</span>
                <div class="delete-icon">
                  <a class="delete is-small" @click.stop="appRemoved(index)"></a>
                </div>
              </button>
            </div>
          </template>
        </Draggable>
      </div>
    </template>

    <div v-if="isLozengeMode" class="add-action" :class="{ 'has-actions': hasSelectedApps }">
      <a @click="showModal = true">
        <AddIcon />
        <span class="add-label">Add action</span>
      </a>
    </div>
  </div>
</template>

<script lang="ts" setup>
import CapableAppModal from './CapableAppModal.vue';
import ContentHighlightsEditModal from '../contentHighlights/ContentHighlightsEditModal.vue';
import { appsApi } from '@/features/apps/shared/services/apps';
import { InstalledApp } from '@/features/apps/shared/models';
import { ConfigRequest } from './configRequest';
import { ConfiguredAppIntegration } from '@/features/apps/shared/models/configuredAppIntegration';
import AddIcon from 'vue-material-design-icons/Plus.vue';
import EditIcon from 'vue-material-design-icons/SquareEditOutline.vue';
import Draggable from 'vuedraggable';
import { computed, onMounted, PropType, ref, watch } from 'vue';
import { useStore } from 'vuex';

const props = defineProps({
  capability: {
    type: String,
    required: true,
  },
  selectedApps: {
    type: Array as PropType<ConfiguredAppIntegration[]>,
    required: true,
  },
  configRequest: {
    type: Function as PropType<() => Promise<ConfigRequest>>,
    required: true,
  },
  modalHeading: {
    type: String,
    required: true,
  },
  isLozengeMode: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  (e: 'update', app: ConfiguredAppIntegration, appIndex: number): void;
  (e: 'change', apps: ConfiguredAppIntegration[]): void;
}>();

const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);

const capableApps = ref<InstalledApp[]>([]);
const showModal = ref(false);
const showAppEditModal = ref(false);
const editingAppIndex = ref<number>();
const apps = ref<ConfiguredAppIntegration[]>([]);

const hasSelectedApps = computed(
  () => !!apps.value && apps.value.length > 0 && capableApps.value && capableApps.value.length > 0,
);

watch(
  () => props.selectedApps,
  (value: ConfiguredAppIntegration) => (apps.value = value),
  { immediate: true },
);

onMounted(() => {
  appsApi.getCapableApps(currentWorkspace.value.id, [props.capability]).then((apps) => {
    capableApps.value = apps.map((app) => app as InstalledApp);
  });
});

function appAdded(installedAppId: string, config: any) {
  apps.value.push({ installedAppId, config });
  emitChangeEvent();
}

function appRemoved(index: number) {
  apps.value.splice(index, 1);
  emitChangeEvent();
}

function appsMoved(e: DraggableChangeEvent) {
  if (e.moved) {
    emitChangeEvent();
  }
}

function getName(installedAppId: string) {
  return getCapableApp(installedAppId).name;
}

function getDescription(installedAppId: string) {
  return getCapableApp(installedAppId).description || 'Private app';
}

function openContentHighlightsEditModal(appIndex: number) {
  showAppEditModal.value = true;
  editingAppIndex.value = appIndex;
}

function handleAppSave(app: ConfiguredAppIntegration, appIndex: number) {
  emit('update', app, appIndex);
  showAppEditModal.value = false;
  editingAppIndex.value = undefined;
}

function canEditApp(app: ConfiguredAppIntegration): boolean {
  const contentHighlightsApp = capableApps.value.find((a: InstalledApp) => a.appId === 'contenthighlights');

  if (!contentHighlightsApp) {
    return false;
  }

  if (app.installedAppId !== contentHighlightsApp.installedAppId) {
    return false;
  }

  return true;
}

function getCapableApp(installedAppId: string) {
  return capableApps.value.find((ca: InstalledApp) => ca.installedAppId === installedAppId) as InstalledApp;
}

function emitChangeEvent() {
  emit('change', apps.value);
}

export interface DraggableChangeEvent {
  moved: {
    newIndex: number;
    oldIndex: number;
    element: Element;
  };
}
</script>

<style lang="scss" scoped>
.table {
  margin-top: 0.5em;
  td.action {
    padding: 0.25rem;
  }
  td.app-title-column {
    width: 90%;
  }
}

.trash-icon,
:deep(.material-design-icon) {
  width: 1.25em;
  height: 1.25em;
  fill: #c7cdd3;
  margin-left: 0.5em;
  cursor: pointer;

  &:hover {
    fill: #a4a6a9;
  }
}

.square-edit-outline-icon {
  cursor: pointer;
  width: 1.25em;
  height: 1.25em;
  svg {
    fill: #c7cdd3;
    width: 1.25em;
    height: 1.25em;
    bottom: -0.25em;
  }
  &:hover {
    svg {
      fill: #a4a6a9;
    }
  }
}

.has-icon {
  vertical-align: middle;
}

.icon-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.app-name {
  color: #363636;
}

.app-description {
  color: #616161;
  opacity: 0.5;
}

.draggable-table {
  display: flex;
  flex-direction: column;
}

.draggable-row {
  display: inline-table;
  cursor: all-scroll;
}

.ghost {
  opacity: 0.5;
}
.add-action {
  display: flex;
  align-items: center;
  height: 2.25em;

  &.has-actions {
    margin-left: 1.4em;
  }
}

.add-label {
  margin-left: 0.25em;
}

.draggable-table-lozenge {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.lozenge-mode {
  display: flex;
  flex-direction: column;
  gap: 30px;

  .delete-icon {
    position: absolute;
    right: 0;
    top: 10px;
    width: 30px;
    visibility: hidden;
    background: inherit;
  }

  .button {
    background-color: #eee;
    border: solid 1px #eee;
    color: inherit;
    min-width: 0;
    opacity: 1;
    .app-name {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      padding-left: 0.5em;
    }

    &:hover {
      color: inherit;
      background-color: #ccc;
      border: solid 1px #ccc;
      cursor: move;
      .delete-icon {
        visibility: visible;
      }
    }

    &:focus {
      color: inherit;
      border-color: #eee;
      outline: none;
    }

    &:focus:not(:active) {
      box-shadow: none;
    }

    &.disabled {
      opacity: 0.6;
      cursor: default;
    }

    .material-design-icon {
      margin-bottom: 2px;
    }
  }
}
</style>
